/**
 * Single source of truth: reconciliation kind -> deep-link destination for
 * the Bank Transactions "Reconciled By" chip.
 *
 * `rb` is a transaction's `reconciled_by` field, server-computed by
 * routes/transactions.py::_batch_reconciled_by.
 *
 * Returns exactly one of:
 *   { type: 'pdf', invoiceId, pdfSource } -- render
 *                                 <InvoicePdfNewTabLink invoiceId={..} pdfSource={..} />,
 *                                 a one-click fetch-and-open-in-a-new-tab
 *                                 control. Driven entirely by rb.pdf_source
 *                                 (routes/transactions.py::
 *                                 _resolve_invoice_pdf_source), computed
 *                                 server-side off the full invoice doc --
 *                                 never the frontend's capped
 *                                 invoicesAPI.getAll() page, so this is
 *                                 correct even for a settling invoice
 *                                 outside that page (2026-08-13 fix: it
 *                                 used to silently fall back to the detail-
 *                                 view link in that case, even when a PDF
 *                                 genuinely existed).
 *   { type: 'link', href }    -- a plain react-router Link.
 *   { type: 'none' }          -- no artifact and no destination; caller
 *                                 renders its own empty-state fallback.
 *
 * 2026-08-13 fix: the invoice kind used to fall back to a `/invoices?
 * search=` list-page link whenever the invoice wasn't in the capped page.
 * That is NEVER the fallback now (CLAUDE.md: never a broken/wrong
 * destination) -- "invoice has no PDF artifact" (rb.pdf_source absent)
 * resolves to the `/invoices?invoice_id=` deep link, which InvoicesPage
 * consumes to auto-open that invoice's OWN detail view (fetching it on
 * demand via GET /invoices/{id} if it isn't already loaded) -- never the
 * bare list.
 */
export function resolveReconciledByDestination(rb) {
  if (!rb || rb.match_missing) return { type: 'none' };

  if (rb.kind === 'invoice') {
    if (rb.pdf_source) return { type: 'pdf', invoiceId: rb.doc_id, pdfSource: rb.pdf_source };
    return { type: 'link', href: `/invoices?invoice_id=${encodeURIComponent(rb.doc_id)}` };
  }

  if (rb.kind === 'management_statement') {
    return { type: 'link', href: `/management-statements?statement_id=${encodeURIComponent(rb.doc_id)}` };
  }

  if (rb.kind === 'recurring_cost' && rb.property_id) {
    const params = new URLSearchParams({ focus: rb.property_id });
    const year = (rb.month || '').slice(0, 4);
    if (year) params.set('year', year);
    return { type: 'link', href: `/costs?${params.toString()}` };
  }

  // invoice_group (settles several invoices -- no single artifact),
  // entity-scoped recurring costs (no property_id to anchor), and any
  // future kind with nothing to deep-link to: sensible existing fallback,
  // never a dead link.
  return { type: 'link', href: '/banking/reconciliation' };
}

/**
 * Compact "under the Reconciled badge" chip label for a BR OS client-invoice
 * (rb.display_ref starts with "BR Revenue") or BR OS supplier-payment
 * auto-reconcile (starts with "BR Supplier") match -- the Bank Transactions
 * Reconciliation column, styled exactly like the recurring-cost "Rent ·
 * 2026-08" chip already there.
 *
 * The invoice number itself NEVER appears here -- it is server-supplied
 * separately as rb.invoice_number for the Reconciled By column
 * (resolveReconciledByLabel below). This function only ever reads
 * rb.display_ref (the type·ENQ / booking-pending label) and never parses an
 * invoice number out of it or vice versa.
 *
 * rb.display_ref is server-computed (routes/transactions.py::
 * _batch_reconciled_by / _bros_revenue_display_ref) and already caps a
 * multi-ENQ group at 3 refs before collapsing the rest into "+N" -- the
 * right amount of detail for the "Reconciled By" column. The compact chip
 * needs to be tighter still (first ref only), so this re-truncates that
 * same string down to one ref + a remainder count, rather than asking the
 * backend to compute and ship a second, differently-capped string. Returns
 * null for every other match kind/source (plain invoice_number matches keep
 * their existing badge, unchanged).
 */
export function resolveReconciliationChipLabel(rb) {
  if (!rb || rb.match_missing || !rb.display_ref) return null;
  if (rb.kind !== 'invoice' && rb.kind !== 'invoice_group') return null;

  const isBrosRevenue = rb.display_ref.startsWith('BR Revenue');
  const isBrosSupplier = rb.display_ref.startsWith('BR Supplier');
  if (!isBrosRevenue && !isBrosSupplier) return null;

  const sepIdx = rb.display_ref.indexOf(' · ');
  if (sepIdx === -1) return rb.display_ref; // defensive: no separator, show as-is

  const label = rb.display_ref.slice(0, sepIdx);
  const refsPart = rb.display_ref.slice(sepIdx + 3);

  if (isBrosSupplier && rb.booking_pending) return `${label} · booking pending`;

  const capMatch = refsPart.match(/^(.*) \+(\d+)$/);
  const refsOnly = capMatch ? capMatch[1] : refsPart;
  const priorRemainder = capMatch ? parseInt(capMatch[2], 10) : 0;

  const refs = refsOnly.split(' + ');
  const extra = (refs.length - 1) + priorRemainder;
  return extra > 0 ? `${label} · ${refs[0]} +${extra}` : `${label} · ${refs[0]}`;
}

/**
 * Deep-link for the Reconciliation-column chip while a BR Supplier match is
 * still "booking pending" (services/wise_supplier_reconciliation.py's
 * bros_push_status is needs_booking_pick/needs_retry, or the push hasn't
 * run yet) -- routes to the Wise Supplier Reconciliation card's own
 * retry/pick row for this match (WiseSupplierReconciliationCard reads the
 * wise_supplier_match_id query param to scroll/highlight it), so the chip
 * is never a bare label that hides the pending link. Returns null once
 * attached, and for every non-supplier chip.
 */
export function resolveReconciliationChipHref(rb) {
  if (!rb || rb.match_missing) return null;
  if (!rb.booking_pending || !rb.match_id) return null;
  return `/diagnostics?wise_supplier_match_id=${encodeURIComponent(rb.match_id)}`;
}

/**
 * Reconciled By column text: THE INVOICE NUMBER, and only the invoice
 * number (Fan's 16 Aug spec) -- never the type·ENQ chip label. Falls back
 * to rb.display_ref for match kinds that have no invoice_number concept
 * (management_statement, recurring_cost).
 */
export function resolveReconciledByLabel(rb) {
  if (!rb) return null;
  return rb.invoice_number ?? rb.display_ref;
}
